import React, { useState, useEffect } from 'react';
import { openPatientScheduling } from '../services/schedulingLinkService';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  Chip,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Menu,
  Link,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  CloudUpload as CloudUploadIcon,
  Mic as MicIcon,
  Summarize as SummarizeIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { meetingService, Meeting, MeetingCreate } from '../services/meetingService';
import { meetingTextService, MeetingText, MeetingTextCreate } from '../services/meetingTextService';
import { patientService, Patient } from '../services/patientService';
import { userService, User } from '../services/userService';
import { meetingTypeService, MeetingType } from '../services/meetingTypeService';
import { activityLogService, ActivityLog, ActivityType } from '../services/activityLogService';
import { fileService, File as FileType } from '../services/fileService';
import { useTenant } from '../context/TenantContext';
import { formatDateTime, formatMeetingDateTime, wallClockToInput, inputToWallClock } from '../utils/dateUtils';
import { getMeetingStatusColor as getStatusColor } from '../utils/statusUtils';
import { useUserTimezone } from '../hooks/useUserTimezone';
import { patientLabel } from '../utils/patientLabel';


// Utility function to detect if text contains Hebrew characters 123
const containsHebrew = (text: string): boolean => {
  const hebrewRegex = /[\u0590-\u05FF]/;
  return hebrewRegex.test(text);
};

// Get text direction and alignment based on content
const getTextDirection = (text: string) => {
  if (containsHebrew(text)) {
    return {
      direction: 'rtl' as const,
      textAlign: 'right' as const,
      fontFamily: 'Arial, sans-serif',
      lineHeight: 1.6
    };
  }
  return {
    direction: 'ltr' as const,
    textAlign: 'left' as const,
    lineHeight: 1.6
  };
};

const MeetingDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userTimezone = useUserTimezone();
  const { currentTenant } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState(0);
  const [addTextDialogOpen, setAddTextDialogOpen] = useState(false);
  const [editingText, setEditingText] = useState<MeetingText | null>(null);
  const [uploadingTranscript, setUploadingTranscript] = useState(false);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [summarizingMeeting, setSummarizingMeeting] = useState(false);

  // Edit meeting state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [therapists, setTherapists] = useState<User[]>([]);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);

  // State for real data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [meetingTexts, setMeetingTexts] = useState<MeetingText[]>([]);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Activity Log State
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsError, setActivityLogsError] = useState<string | null>(null);
  const [activityLogSearch, setActivityLogSearch] = useState('');
  const [activityLogType, setActivityLogType] = useState<ActivityType | ''>('');
  const [activityLogPage, setActivityLogPage] = useState(0);
  const [activityLogRowsPerPage, setActivityLogRowsPerPage] = useState(25);
  const [activityLogTotal, setActivityLogTotal] = useState(0);

  // Files State
  const [files, setFiles] = useState<FileType[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Load meeting data
  useEffect(() => {
    const loadMeetingData = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const meetingId = parseInt(id);
        const [meetingData, textsData] = await Promise.all([
          meetingService.getMeeting(meetingId),
          meetingTextService.getMeetingTexts(meetingId),
        ]);

        setMeeting(meetingData);
        setMeetingTexts(textsData);
      } catch (err) {
        console.error('Error loading meeting data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load meeting data');
      } finally {
        setLoading(false);
      }
    };

    loadMeetingData();
  }, [id]);

  // Fetch activity logs for the meeting
  useEffect(() => {
    const fetchActivityLogs = async () => {
      if (!meeting || activeTab !== 2) return;

      setActivityLogsLoading(true);
      setActivityLogsError(null);
      try {
        const response = await activityLogService.getActivityLogs({
          page: activityLogPage + 1,
          page_size: activityLogRowsPerPage,
          search: activityLogSearch || undefined,
          activity_type: activityLogType || undefined,
          sort_by: 'created_at',
          sort_order: 'desc',
        });

        // Filter logs related to this meeting
        const meetingLogs = response.items.filter(
          log => log.meeting_id === meeting.id
        );

        setActivityLogs(meetingLogs);
        setActivityLogTotal(meetingLogs.length);
      } catch (err: unknown) {
        const error = err as Error;
        setActivityLogsError(error.message || 'Failed to fetch activity logs');
      } finally {
        setActivityLogsLoading(false);
      }
    };

    fetchActivityLogs();
  }, [meeting, activeTab, activityLogPage, activityLogRowsPerPage, activityLogSearch, activityLogType]);

  // Fetch files for the meeting
  useEffect(() => {
    const fetchFiles = async () => {
      if (!meeting || activeTab !== 1) return;

      setFilesLoading(true);
      try {
        const response = await fileService.getFiles(undefined, meeting.id);
        setFiles(response.items);
      } catch (error) {
        console.error('Error fetching files:', error);
        toast.error('Failed to load files');
      } finally {
        setFilesLoading(false);
      }
    };

    fetchFiles();
  }, [meeting, activeTab]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !event.target.files[0] || !meeting) return;

    const file = event.target.files[0];
    setUploadingFile(true);

    try {
      await fileService.uploadFile(file, undefined, undefined, meeting.id);
      toast.success('File uploaded successfully');

      // Refresh files list
      const response = await fileService.getFiles(undefined, meeting.id);
      setFiles(response.items);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleFileDownload = async (fileId: number) => {
    try {
      await fileService.downloadFile(fileId);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleFileDelete = async (fileId: number) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await fileService.deleteFile(fileId);
      toast.success('File deleted successfully');

      // Refresh files list
      if (meeting) {
        const response = await fileService.getFiles(undefined, meeting.id);
        setFiles(response.items);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };

  const { register: registerText, handleSubmit: handleSubmitText, reset: resetText, formState: { errors } } = useForm<MeetingTextCreate>();
  const { register: registerMeeting, handleSubmit: handleSubmitMeeting, reset: resetMeeting, watch, setValue, formState: { errors: meetingErrors } } = useForm<MeetingCreate>();

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'transcript':
        return 'secondary';
      case 'summary':
        return 'primary';
      case 'notes':
        return 'success';
      case 'treatment_plan':
        return 'info';
      case 'homework':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUpdateMeetingStatus = async (status: 'active' | 'noshow' | 'cancelled' | 'done') => {
    if (!meeting) return;

    try {
      await meetingService.updateMeetingStatus(meeting.id, status);
      toast.success(`Meeting marked as ${status}`);
      setMeeting({ ...meeting, status });
    } catch (error) {
      console.error('Error updating meeting status:', error);
      toast.error('Failed to update meeting status');
    }
    handleMenuClose();
  };

  const handleEditText = (text: MeetingText) => {
    setEditingText(text);
    resetText({
      title: text.title,
      content: text.content,
      text_type: text.text_type,
    });
    setAddTextDialogOpen(true);
  };

  const handleAddText = () => {
    setEditingText(null);
    resetText();
    setAddTextDialogOpen(true);
  };

  const onSubmitText = async (data: MeetingTextCreate) => {
    if (!meeting) return;

    try {
      const textData = {
        ...data,
        meeting_id: meeting.id,
      };

      if (editingText) {
        // Update existing text
        const updatedText = await meetingTextService.updateMeetingText(editingText.id, textData);
        setMeetingTexts(meetingTexts.map(t => t.id === editingText.id ? updatedText : t));
        toast.success('Meeting text updated successfully');
      } else {
        // Create new text
        const newText = await meetingTextService.createMeetingText(textData);
        setMeetingTexts([...meetingTexts, newText]);
        toast.success('Meeting text created successfully');
      }

      setAddTextDialogOpen(false);
      setEditingText(null);
      resetText();
    } catch (error) {
      console.error('Error saving meeting text:', error);
      toast.error('Failed to save meeting text');
    }
  };

  // Check if a meeting text is deletable (not AI-generated)
  const isTextEditable = (text: MeetingText): boolean => {
    return !text.is_ai_generated;
  };

  const handleDeleteText = async (textId: number) => {
    try {
      await meetingTextService.deleteMeetingText(textId);
      setMeetingTexts(meetingTexts.filter(t => t.id !== textId));
      toast.success('Meeting text deleted successfully');
    } catch (error) {
      console.error('Error deleting meeting text:', error);
      toast.error('Failed to delete meeting text');
    }
  };

  const handleTranscriptUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !meeting) return;

    // Validate file type
    const allowedTypes = ['.txt', '.doc', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      toast.error('Only .txt, .doc, and .docx files are allowed');
      return;
    }

    try {
      setUploadingTranscript(true);
      const newText = await meetingTextService.uploadTranscript(meeting.id, file);
      setMeetingTexts([...meetingTexts, newText]);
      toast.success('Transcript uploaded successfully');
      // Clear the input
      event.target.value = '';
    } catch (error) {
      console.error('Error uploading transcript:', error);
      toast.error('Failed to upload transcript');
    } finally {
      setUploadingTranscript(false);
    }
  };

  const handleRecordingUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !meeting) return;

    // Validate file type
    const allowedTypes = ['.mp3', '.mp4', '.mpeg', '.mpga', '.m4a', '.wav', '.webm', '.flac', '.ogg', '.aac'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      toast.error('Only audio/video files are allowed (mp3, mp4, wav, etc.)');
      return;
    }

    // Check file size (25MB limit)
    const maxSize = 25 * 1024 * 1024; // 25MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 25MB');
      return;
    }

    try {
      setUploadingRecording(true);
      toast('Starting transcription... This will be processed in the background.');

      const jobResponse = await meetingTextService.uploadRecording(meeting.id, file, 'he');
      toast.success(`Transcription job created (ID: ${jobResponse.job_id}). Check back later for results.`);
      // Clear the input
      event.target.value = '';

      // Optionally refresh the meeting texts after a delay to check for completed transcription
      setTimeout(async () => {
        if (meeting) {
          const textsData = await meetingTextService.getMeetingTexts(meeting.id);
          setMeetingTexts(textsData);
        }
      }, 5000); // Check again in 5 seconds
    } catch (error) {
      console.error('Error uploading recording:', error);
      toast.error('Failed to create transcription job');
    } finally {
      setUploadingRecording(false);
    }
  };

  const handleSummarizeMeeting = async () => {
    if (!meeting) return;

    // Check if there are any texts to summarize
    if (meetingTexts.length === 0) {
      toast.error('No content available to summarize. Add notes, transcripts, or other meeting texts first.');
      return;
    }

    try {
      setSummarizingMeeting(true);
      toast('Starting meeting summarization... This will be processed in the background.');

      const jobResponse = await meetingService.summarizeMeeting(meeting.id);
      toast.success(`Summarization job created (ID: ${jobResponse.job_id}). Check back later for results.`);

      // Optionally refresh the meeting texts after a delay to check for completed summary
      setTimeout(async () => {
        if (meeting) {
          const textsData = await meetingTextService.getMeetingTexts(meeting.id);
          setMeetingTexts(textsData);
        }
      }, 10000); // Check again in 10 seconds (summarization takes longer)
    } catch (error) {
      console.error('Error creating summarization job:', error);
      toast.error('Failed to create summarization job');
    } finally {
      setSummarizingMeeting(false);
    }
  };

  const handleEditAISummary = async (text: MeetingText) => {
    if (!meeting) return;

    try {
      // Create a new manual text based on the AI-generated one
      const textData = {
        title: text.title,
        content: text.content,
        text_type: text.text_type,
        name: text.name || undefined,
        meeting_id: meeting.id,
      };

      const newText = await meetingTextService.createMeetingText(textData);
      setMeetingTexts([...meetingTexts, newText]);

      toast.success('Created editable copy of AI summary. You can now edit it manually.');

      // Auto-open the edit dialog for the new text
      setEditingText(newText);
      resetText({
        title: newText.title,
        content: newText.content,
        text_type: newText.text_type,
      });
      setAddTextDialogOpen(true);
    } catch (error) {
      console.error('Error creating editable copy of AI summary:', error);
      toast.error('Failed to create editable copy');
    }
  };

  const handleEditMeeting = async () => {
    if (!meeting || !currentTenant) return;

    try {
      // Load patients, therapists, and meeting types for the form
      const [patientsData, usersData, meetingTypesData] = await Promise.all([
        patientService.getAllPatients(currentTenant.id),
        userService.getUsers(currentTenant.id),
        meetingTypeService.getMeetingTypes(currentTenant.id),
      ]);

      setPatients(patientsData);
      setMeetingTypes(meetingTypesData);
      // All users can schedule for any therapist.
      setTherapists(usersData);

      // Pre-populate the form with current meeting data
      resetMeeting({
        title: meeting.title,
        scheduled_at: wallClockToInput(meeting.scheduled_at),
        duration_minutes: meeting.duration_minutes,
        notes: meeting.notes || '',
        fee: meeting.fee || 0,
        patient_id: meeting.patient_id ?? undefined,
        therapist_id: meeting.therapist_id,
        tenant_id: meeting.tenant_id,
        meeting_type_id: meeting.meeting_type_id || undefined,
      });

      setEditDialogOpen(true);
    } catch (error) {
      console.error('Error loading edit meeting data:', error);
      toast.error('Failed to load edit meeting data');
    }
  };

  const onSubmitMeetingEdit = async (data: MeetingCreate) => {
    if (!meeting || !currentTenant) {
      toast.error('No meeting or tenant data available');
      return;
    }

    try {
      const meetingData = {
        ...data,
        // Convert Jerusalem datetime input to UTC for server
        scheduled_at: inputToWallClock(data.scheduled_at),
        tenant_id: currentTenant.id,
      };

      const updatedMeeting = await meetingService.updateMeeting(meeting.id, meetingData);
      setMeeting(updatedMeeting);

      toast.success('Meeting updated successfully');
      setEditDialogOpen(false);
      resetMeeting();
    } catch (error) {
      console.error('Error updating meeting:', error);
      toast.error('Failed to update meeting');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !meeting) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading meeting: {error || 'Meeting not found'}
      </Alert>
    );
  }

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexDirection={{ xs: 'column', md: 'row' }} gap={{ xs: 3, md: 0 }}>
          <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }} gutterBottom>
              {meeting.title}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
              {formatMeetingDateTime(meeting.scheduled_at, userTimezone)} • {meeting.duration_minutes} minutes
            </Typography>
          </Box>

          {/* Mobile: Chip and Buttons */}
          {isMobile ? (
            <Box display="flex" flexDirection="column" width="100%" gap={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEditMeeting}
                  size="small"
                  sx={{ fontSize: '0.75rem', flex: 1 }}
                >
                  Edit
                </Button>
                {meeting.patient?.uuid && (
                  <Button
                    variant="outlined"
                    startIcon={<OpenInNewIcon />}
                    onClick={() => openPatientScheduling(meeting.patient?.uuid, { meetingId: meeting.id }, meeting.patient?.id)}
                    size="small"
                    sx={{ fontSize: '0.75rem', flex: 1 }}
                    color="secondary"
                  >
                    Schedule
                  </Button>
                )}
              </Box>
              <Box display="flex" justifyContent="flex-end" width="100%">
                <Chip
                  label={meeting.status.toUpperCase()}
                  color={getStatusColor(meeting.status) as any}
                  size="medium"
                  onClick={handleMenuClick}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Box>
          ) : (
            /* Desktop: Chip and Buttons */
            <Box display="flex" gap={2} alignItems="center">
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditMeeting}
              >
                Edit Meeting
              </Button>
              {meeting.patient?.uuid && (
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  // The patient's whole schedule, not this one event: moving
                  // a meeting in isolation is what left the rest of their
                  // chain inconsistent.
                  onClick={() => openPatientScheduling(meeting.patient?.uuid, { meetingId: meeting.id }, meeting.patient?.id)}
                  color="secondary"
                >
                  Update Schedule
                </Button>
              )}
              <Chip
                label={meeting.status.toUpperCase()}
                color={getStatusColor(meeting.status) as any}
                onClick={handleMenuClick}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Patient Information
            </Typography>
            <Typography>
              <strong>Name:</strong>{' '}
              <Link
                component="button"
                variant="body1"
                onClick={() => meeting.patient && navigate(`/patients/${meeting.patient.id}`)}
                sx={{ textAlign: 'left',
                      cursor: meeting.patient ? 'pointer' : 'default',
                      textDecoration: meeting.patient ? undefined : 'none' }}
              >
                {patientLabel(meeting)}
              </Link>
            </Typography>
            <Typography><strong>Email:</strong> {meeting.patient?.email || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Therapist
            </Typography>
            <Typography><strong>Name:</strong> {meeting.therapist.full_name}</Typography>
            <Typography><strong>Email:</strong> {meeting.therapist.email}</Typography>
          </Grid>
        </Grid>

        {meeting.notes && (
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              Meeting Notes
            </Typography>
            <Typography sx={getTextDirection(meeting.notes)}>
              {meeting.notes}
            </Typography>
          </Box>
        )}

        {meeting.meeting_type && (
          <Box mt={2}>
            <Typography><strong>Meeting Type:</strong> {meeting.meeting_type.name}</Typography>
          </Box>
        )}

        {meeting.fee && (
          <Box mt={1}>
            <Typography><strong>Fee:</strong> ${meeting.fee}</Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ px: 3 }}
          >
            <Tab label="Meeting Texts" />
            <Tab label="Files" />
            <Tab label="Activity Logs" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {activeTab === 0 && (
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                mb={3}
                flexDirection={{ xs: 'column', md: 'row' }}
                gap={{ xs: 2, md: 0 }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                >
                  Meeting Texts ({meetingTexts.length})
                </Typography>

                {/* Desktop buttons */}
                <Box
                  display={{ xs: 'none', md: 'flex' }}
                  gap={2}
                >
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddText}
                  >
                    Add Text
                  </Button>
                  <input
                    type="file"
                    accept=".vtt,.txt"
                    onChange={handleTranscriptUpload}
                    style={{ display: 'none' }}
                    id="transcript-upload-input"
                  />
                  <label htmlFor="transcript-upload-input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      disabled={uploadingTranscript}
                    >
                      {uploadingTranscript ? 'Uploading...' : 'Upload Transcript'}
                    </Button>
                  </label>
                  <input
                    type="file"
                    accept=".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm,.flac,.ogg,.aac"
                    onChange={handleRecordingUpload}
                    style={{ display: 'none' }}
                    id="recording-upload-input"
                  />
                  <label htmlFor="recording-upload-input">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<MicIcon />}
                      disabled={uploadingRecording}
                      color="secondary"
                    >
                      {uploadingRecording ? 'Transcribing...' : 'Upload Recording'}
                    </Button>
                  </label>
                  <Button
                    variant="outlined"
                    startIcon={<SummarizeIcon />}
                    onClick={handleSummarizeMeeting}
                    disabled={summarizingMeeting || meetingTexts.length === 0}
                    color="primary"
                  >
                    {summarizingMeeting ? 'Summarizing...' : 'Summarize Meeting'}
                  </Button>
                </Box>

                {/* Mobile buttons - two line layout */}
                <Box
                  display={{ xs: 'block', md: 'none' }}
                  width="100%"
                >
                  {/* First row - Add Text and Transcript */}
                  <Box display="flex" gap={1} mb={1}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddText}
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      Add Text
                    </Button>
                    <input
                      type="file"
                      accept=".vtt,.txt"
                      onChange={handleTranscriptUpload}
                      style={{ display: 'none' }}
                      id="transcript-upload-input-mobile"
                    />
                    <label htmlFor="transcript-upload-input-mobile" style={{ flex: 1 }}>
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<CloudUploadIcon />}
                        disabled={uploadingTranscript}
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          width: '100%',
                          minWidth: 0
                        }}
                      >
                        {uploadingTranscript ? 'Uploading...' : 'Transcript'}
                      </Button>
                    </label>
                  </Box>

                  {/* Second row - Recording and Summarize */}
                  <Box display="flex" gap={1}>
                    <input
                      type="file"
                      accept=".mp3,.mp4,.mpeg,.mpga,.m4a,.wav,.webm,.flac,.ogg,.aac"
                      onChange={handleRecordingUpload}
                      style={{ display: 'none' }}
                      id="recording-upload-input-mobile"
                    />
                    <label htmlFor="recording-upload-input-mobile" style={{ flex: 1 }}>
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<MicIcon />}
                        disabled={uploadingRecording}
                        color="secondary"
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          width: '100%',
                          minWidth: 0
                        }}
                      >
                        {uploadingRecording ? 'Transcribing...' : 'Recording'}
                      </Button>
                    </label>
                    <Button
                      variant="outlined"
                      startIcon={<SummarizeIcon />}
                      onClick={handleSummarizeMeeting}
                      disabled={summarizingMeeting || meetingTexts.length === 0}
                      color="primary"
                      size="small"
                      sx={{
                        fontSize: '0.75rem',
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      {summarizingMeeting ? 'Summarizing...' : 'Summarize'}
                    </Button>
                  </Box>
                </Box>
              </Box>

              {meetingTexts.map((text) => (
                <Accordion key={text.id} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      display="flex"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      flexDirection={{ xs: 'column', sm: 'row' }}
                      gap={{ xs: 1, sm: 2 }}
                      width="100%"
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          flexGrow: 1,
                          fontSize: { xs: '1rem', sm: '1.25rem' },
                          lineHeight: { xs: 1.3, sm: 1.6 }
                        }}
                      >
                        {text.title}
                      </Typography>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip
                          label={text.text_type}
                          color={getTypeColor(text.text_type) as any}
                          size="small"
                          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                        />
                        {text.is_ai_generated && (
                          <Chip
                            label="AI"
                            color="secondary"
                            size="small"
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                          />
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 2,
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          ...getTextDirection(text.content)
                        }}
                      >
                        {text.content}
                      </Typography>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        gap={{ xs: 2, sm: 0 }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          Created by {text.created_by?.full_name ?? (text.is_ai_generated ? 'AI' : 'System')} on {formatDateTime(text.created_at)}
                          {text.updated_at !== text.created_at && (
                            <> • Updated {formatDateTime(text.updated_at)}</>
                          )}
                          {text.word_count && <> • {text.word_count} words</>}
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {!text.is_ai_generated && (
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditText(text)}
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                            >
                              Edit
                            </Button>
                          )}
                          {text.is_ai_generated && text.text_type === 'summary' && (
                            <Button
                              size="small"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditAISummary(text)}
                              variant="outlined"
                              color="secondary"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                            >
                              Edit Summary
                            </Button>
                          )}
                          {isTextEditable(text) && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleDeleteText(text.id)}
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                            >
                              Delete
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Files ({files.length})
                </Typography>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploadingFile}
                >
                  {uploadingFile ? 'Uploading...' : 'Upload File'}
                  <input
                    type="file"
                    hidden
                    onChange={handleFileUpload}
                    accept="*/*"
                  />
                </Button>
              </Box>

              {filesLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : files.length === 0 ? (
                <Typography color="text.secondary" align="center" py={4}>
                  No files uploaded yet
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>File Name</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Uploaded By</TableCell>
                        <TableCell>Uploaded At</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {files.map((file) => (
                        <TableRow key={file.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <FileIcon color="action" />
                              {file.file_name}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {(file.file_size / 1024).toFixed(2)} KB
                          </TableCell>
                          <TableCell>{file.uploaded_by_name}</TableCell>
                          <TableCell>
                            {formatDateTime(file.created_at)}
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleFileDownload(file.id)}
                              title="Download"
                            >
                              <DownloadIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleFileDelete(file.id)}
                              title="Delete"
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              {/* Search and Filter Controls */}
              <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={activityLogSearch}
                  onChange={(e) => {
                    setActivityLogSearch(e.target.value);
                    setActivityLogPage(0);
                  }}
                  sx={{ flexGrow: 1, minWidth: 200 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Activity Type</InputLabel>
                  <Select
                    value={activityLogType}
                    label="Activity Type"
                    onChange={(e) => {
                      setActivityLogType(e.target.value as ActivityType | '');
                      setActivityLogPage(0);
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="meeting_created">Meeting Created</MenuItem>
                    <MenuItem value="meeting_updated">Meeting Updated</MenuItem>
                    <MenuItem value="meeting_status_changed">Meeting Status Changed</MenuItem>
                    <MenuItem value="meeting_text_created">Meeting Text Created</MenuItem>
                    <MenuItem value="meeting_text_updated">Meeting Text Updated</MenuItem>
                    <MenuItem value="meeting_text_deleted">Meeting Text Deleted</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {activityLogsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {activityLogsError}
                </Alert>
              )}

              {/* Activity Logs Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Activity Type</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activityLogsLoading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : activityLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No activity logs found for this meeting
                        </TableCell>
                      </TableRow>
                    ) : (
                      activityLogs.map((log) => (
                        <TableRow key={log.id} hover>
                          <TableCell>
                            {formatDateTime(log.created_at, {}, userTimezone)}
                          </TableCell>
                          <TableCell>{log.user_name || `User #${log.user_id}`}</TableCell>
                          <TableCell>
                            <Chip
                              label={log.activity_type
                                .split('_')
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')}
                              color={
                                log.activity_type.includes('created')
                                  ? 'success'
                                  : log.activity_type.includes('updated')
                                  ? 'info'
                                  : log.activity_type.includes('deleted')
                                  ? 'error'
                                  : log.activity_type.includes('status')
                                  ? 'warning'
                                  : 'default'
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{log.description}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={activityLogTotal}
                  rowsPerPage={activityLogRowsPerPage}
                  page={activityLogPage}
                  onPageChange={(_, newPage) => setActivityLogPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setActivityLogRowsPerPage(parseInt(event.target.value, 10));
                    setActivityLogPage(0);
                  }}
                />
              </TableContainer>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Add/Edit Text Dialog */}
      <Dialog
        open={addTextDialogOpen}
        onClose={() => setAddTextDialogOpen(false)}
        maxWidth={isMobile ? false : "md"}
        fullWidth={!isMobile}
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: 2 },
            width: { xs: '100vw', sm: '100%' },
            height: { xs: '100vh', sm: 'auto' },
            maxHeight: { xs: '100vh', sm: '90vh' },
            borderRadius: { xs: 0, sm: 1 }
          }
        }}
      >
        <form onSubmit={handleSubmitText(onSubmitText)}>
          <DialogTitle sx={{
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            pb: { xs: 1, sm: 2 }
          }}>
            {editingText ? 'Edit Text' : 'Add New Text'}
          </DialogTitle>
          <DialogContent sx={{
            px: { xs: 1.5, sm: 3 },
            py: { xs: 1, sm: 2 },
            overflow: 'auto'
          }}>
            <TextField
              {...registerText('title', { required: 'Title is required' })}
              fullWidth
              label="Title"
              margin="normal"
              error={!!errors.title}
              helperText={errors.title?.message as string}
              defaultValue={editingText?.title || ''}
              size={isMobile ? 'small' : 'medium'}
              sx={{ mb: { xs: 2, sm: 3 } }}
            />
            <FormControl
              fullWidth
              margin="normal"
              error={!!errors.text_type}
              size={isMobile ? 'small' : 'medium'}
              sx={{ mb: { xs: 2, sm: 3 } }}
            >
              <InputLabel>Type</InputLabel>
              <Select
                {...registerText('text_type', { required: 'Type is required' })}
                label="Type"
                defaultValue={editingText?.text_type || 'notes'}
              >
                <MenuItem value="transcript">Transcript</MenuItem>
                <MenuItem value="summary">Summary</MenuItem>
                <MenuItem value="notes">Notes</MenuItem>
                <MenuItem value="treatment_plan">Treatment Plan</MenuItem>
                <MenuItem value="homework">Homework</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <TextField
              {...registerText('content', { required: 'Content is required' })}
              fullWidth
              label="Content"
              multiline
              rows={isMobile ? 4 : 6}
              margin="normal"
              error={!!errors.content}
              helperText={errors.content?.message as string}
              defaultValue={editingText?.content || ''}
              size={isMobile ? 'small' : 'medium'}
              InputProps={{
                sx: {
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  ...getTextDirection(editingText?.content || ''),
                  '& textarea': {
                    direction: containsHebrew(editingText?.content || '') ? 'rtl' : 'ltr',
                    textAlign: containsHebrew(editingText?.content || '') ? 'right' : 'left'
                  }
                }
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{
              px: { xs: 1.5, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: 'column', sm: 'row' }
            }}
          >
            <Button
              onClick={() => setAddTextDialogOpen(false)}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                order: { xs: 2, sm: 1 }
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                width: { xs: '100%', sm: 'auto' },
                order: { xs: 1, sm: 2 }
              }}
            >
              {editingText ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Meeting Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <form onSubmit={handleSubmitMeeting(onSubmitMeetingEdit)}>
          <DialogTitle>
            Edit Meeting
          </DialogTitle>
          <DialogContent>
            <TextField
              {...registerMeeting('title', { required: 'Title is required' })}
              fullWidth
              label="Title"
              margin="normal"
              error={!!meetingErrors.title}
              helperText={meetingErrors.title?.message as string}
            />
            <FormControl fullWidth margin="normal" error={!!meetingErrors.patient_id}>
              <InputLabel>Patient</InputLabel>
              <Select
                {...registerMeeting('patient_id', { required: 'Patient is required', valueAsNumber: true })}
                label="Patient"
                value={watch('patient_id') || ''}
                onChange={(e) => setValue('patient_id', e.target.value as number)}
              >
                {patients.map((patient) => (
                  <MenuItem key={patient.id} value={patient.id}>
                    {patient.full_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" error={!!meetingErrors.therapist_id}>
              <InputLabel>Therapist</InputLabel>
              <Select
                {...registerMeeting('therapist_id', { required: 'Therapist is required', valueAsNumber: true })}
                label="Therapist"
                value={watch('therapist_id') || ''}
                onChange={(e) => setValue('therapist_id', e.target.value as number)}
              >
                {therapists.map((therapist) => (
                  <MenuItem key={therapist.id} value={therapist.id}>
                    {therapist.full_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              {...registerMeeting('scheduled_at', { required: 'Scheduled time is required' })}
              fullWidth
              label="Scheduled At"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              margin="normal"
              error={!!meetingErrors.scheduled_at}
              helperText={meetingErrors.scheduled_at?.message as string}
            />
            <TextField
              {...registerMeeting('duration_minutes', { valueAsNumber: true })}
              fullWidth
              label="Duration (minutes)"
              type="number"
              margin="normal"
              defaultValue={50}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Meeting Type</InputLabel>
              <Select
                {...registerMeeting('meeting_type_id', { valueAsNumber: true })}
                label="Meeting Type"
                value={watch('meeting_type_id') || ''}
                onChange={(e) => setValue('meeting_type_id', e.target.value ? Number(e.target.value) : undefined)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {meetingTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              {...registerMeeting('fee', { valueAsNumber: true })}
              fullWidth
              label="Fee"
              type="number"
              margin="normal"
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              {...registerMeeting('notes')}
              fullWidth
              label="Notes"
              multiline
              rows={3}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Update Meeting
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Status Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {meeting?.status === 'active' && (
          <>
            <MenuItem onClick={() => handleUpdateMeetingStatus('done')}>Mark as Done</MenuItem>
            <MenuItem onClick={() => handleUpdateMeetingStatus('cancelled')}>Cancel</MenuItem>
            <MenuItem onClick={() => handleUpdateMeetingStatus('noshow')}>Mark as No-Show</MenuItem>
          </>
        )}
        {(meeting?.status === 'noshow' || meeting?.status === 'cancelled' || meeting?.status === 'done') && (
          <MenuItem onClick={() => handleUpdateMeetingStatus('active')}>Reactivate</MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default MeetingDetailPage;